import { useEffect, useState, useMemo, useRef } from 'react';
import { Alert } from '@mui/material';
import { ProjectResourceModel } from 'store/userOrg/userOrgApi';

interface QpuStatusAlertsProps {
    projectResources?: ProjectResourceModel[];
}

const FETCH_TIMEOUT_MS = 8000;
const POLLING_INTERVAL_MS = 30000;

interface QpuEndpoint {
    id: string;
    resourceName: string;
    endpoint: string;
}

const useQpuStatuses = (projectResources?: ProjectResourceModel[]) => {
    const [offlineQpus, setOfflineQpus] = useState<string[]>([]);
    const activeControllersRef = useRef<AbortController[]>([]);
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

    const endpoints = useMemo<QpuEndpoint[]>(() => {
        if (!projectResources) return [];
        const found: QpuEndpoint[] = [];
        for (const res of projectResources) {
            if (!res.Assignments) continue;
            for (const assignment of res.Assignments) {
                const spec = assignment.Specifications?.find(s => s.Key === 'QPU_STATUS_ENDPOINT');
                if (spec?.Value) {
                    found.push({
                        id: res.Id || `${res.Name}-${spec.Value}`,
                        resourceName: res.Name || 'Unknown QPU',
                        endpoint: spec.Value,
                    });
                }
            }
        }
        return found;
    }, [projectResources]);

    const endpointsKey = useMemo(() => endpoints.map(e => `${e.id}-${e.endpoint}`).join(','), [endpoints]);

    useEffect(() => {
        let isCurrent = true;

        const cleanupActiveOperations = () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
            }
            activeControllersRef.current.forEach(controller => controller.abort());
            activeControllersRef.current = [];
        };

        if (endpoints.length === 0) {
            cleanupActiveOperations();
            setOfflineQpus([]);
            return;
        }

        const checkStatuses = async () => {
            cleanupActiveOperations();
            const offlineList: string[] = [];

            try {
                await Promise.all(
                    endpoints.map(async ({ resourceName, endpoint }) => {
                        const controller = new AbortController();
                        activeControllersRef.current.push(controller);
                        const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

                        try {
                            const response = await fetch(endpoint, { signal: controller.signal });
                            clearTimeout(timeoutId);

                            if (!response.ok) return;

                            const data = await response.json();
                            if (data?.total_status === false) {
                                offlineList.push(resourceName);
                            }
                        } catch (error) {
                            clearTimeout(timeoutId);
                            const isAbort = error instanceof DOMException && error.name === 'AbortError';
                            if (process.env.NODE_ENV === 'development' && !isAbort) {
                                console.error(`Error checking QPU status for ${resourceName}:`, error);
                            }
                        }
                    })
                );

                if (isCurrent) {
                    setOfflineQpus(offlineList);
                    timeoutIdRef.current = setTimeout(checkStatuses, POLLING_INTERVAL_MS);
                }
            } catch (e) {
                if (process.env.NODE_ENV === 'development' && isCurrent) {
                    console.error('Failed to run batch QPU status check:', e);
                }
            }
        };

        checkStatuses();

        return () => {
            isCurrent = false;
            cleanupActiveOperations();
        };
    }, [endpointsKey]);

    return { offlineQpus, endpoints };
};

export const QpuStatusAlerts = ({ projectResources }: QpuStatusAlertsProps) => {
    const { offlineQpus, endpoints } = useQpuStatuses(projectResources);

    if (offlineQpus.length === 0) {
        return null;
    }

    return (
        <>
            {offlineQpus.map((qpuName, index) => {
                const qpuInfo = endpoints.find(e => e.resourceName === qpuName);
                const key = qpuInfo?.id || `offline-${qpuName}-${index}`;
                return (
                    <Alert key={key} severity='error' sx={{ mb: 2 }}>
                        QPU {qpuName} is currently OFFLINE
                    </Alert>
                );
            })}
        </>
    );
};
